import React, { useMemo, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import ModalForms from '../../Shared/Modals/index.js';
import CampaignAssignedDropdown from '../../Shared/CampaignAssignmentDropdown/index.tsx';
import { useRecoilValue } from 'recoil';
import { campaignLeadManagementAtom } from '../../../Services/_states/index.js';
import SearchableDropdown from '../../FlowBuilder/Common/SearchableDropdown/SearchableDropdown.tsx';
import { LeadFormActions } from '../../../Services/_actions/index.js';
import { staticColumnsMatch } from '../DynamicFormFields/helper.ts';

interface MoveToModalProps {
  open: boolean;
  onClose: () => void;
  selectedLeadIds: string[];
  organisationId: string;
}

const mapFormsToOptions = (formsObj: any = {}) =>
  Object.values(formsObj).map((form: any) => ({
    label: form.leads_form_name,
    value: form.leads_form_id,
    ...form,
  }));

const mapFormItemsToOptions = (items: any = {}) =>
  Object.values(items)
    .sort((a: any, b: any) => a.order_id - b.order_id)
    .map((item: any) => ({
      label: item.alias_name,
      value: item.key_name,
      ...item,
    }));

const MoveToModal: React.FC<MoveToModalProps> = ({
  open,
  onClose,
  selectedLeadIds,
  organisationId,
}) => {
  const { showSnackBar } = useSnackBar();
  const campaignLeadContext = useRecoilValue(campaignLeadManagementAtom);
  const action = LeadFormActions();
  const [targetCampaign, setTargetCampaign] = useState<any>(null);
  const [targetForm, setTargetForm] = useState<any>(null);
  const [targetForms, setTargetForms] = useState<any[]>([]);
  const [targetFormItems, setTargetFormItems] = useState<any[]>([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [selectedTargetFormItems, setSelectedTargetFormItems] = useState<any[]>([]);

  const filterFormItems = (items: any[] = []) => {
    return items.filter(
      (item) =>
        !item.is_static_column &&
        item.statusOption !== 'is_status' &&
        item.statusOption !== 'is_sub_status' &&
        item.key_name !== 'lead_id'
    );
  };

  const currentCampaign = useMemo(() => {
    const campaign = campaignLeadContext?.selectedCampaign;
    if (!campaign) return null;
    return {
      proposal_id: campaign.proposal_id,
      name: campaign.name || 'Current Campaign',
    };
  }, [campaignLeadContext]);

  const currentFormsOptions = useMemo(() => {
    if (!currentCampaign) return [];
    return (campaignLeadContext?.[currentCampaign.proposal_id] || []).map(
      (form: any) => ({
        label: form.leads_form_name,
        value: form.leads_form_id,
        ...form,
      })
    );
  }, [campaignLeadContext, currentCampaign]);

  const currentForm = campaignLeadContext?.selectedForm
    ? {
      label: campaignLeadContext.selectedForm.leads_form_name,
      value: campaignLeadContext.selectedForm.leads_form_id,
      ...campaignLeadContext.selectedForm,
    }
    : null;

  const currentFormItems = useMemo(() => {
    const items = mapFormItemsToOptions(
      campaignLeadContext?.selectedForm?.leads_form_items || {}
    );
    return filterFormItems(items);
  }, [campaignLeadContext]);


  const handleTargetCampaignChange = async (val: any) => {
    setTargetCampaign(val);
    setTargetForm(null);
    setTargetFormItems([]);
    setTargetForms([]);

    if (!val) return;

    try {
      setLoadingForms(true);
      const res = await action.getFormsByCampaignId(val.proposal_id);
      const forms = res?.data || {};
      setTargetForms(mapFormsToOptions(forms));
    } catch (err) {
      showSnackBar('Failed to load forms', 'error');
    } finally {
      setLoadingForms(false);
    }
  };

  const handleTargetFormChange = (form: any) => {
    setTargetForm(form);
    if (form?.leads_form_items) {
      const items = mapFormItemsToOptions(form.leads_form_items);
      setTargetFormItems(filterFormItems(items));
    } else {
      setTargetFormItems([]);
    }
  };

  const isSameCampaign =
    targetCampaign?.proposal_id === currentCampaign?.proposal_id;

  const handleMove = async () => {
    if (!targetCampaign || !targetForm) {
      showSnackBar('Please select campaign and form', 'error');
      return;
    }
    const leadIdsArray = Array.isArray(selectedLeadIds)
      ? selectedLeadIds
      : Object.keys(selectedLeadIds).filter((id) => selectedLeadIds[id]);

    if (leadIdsArray.length === 0) {
      showSnackBar('No leads selected', 'error');
      return;
    }

    const key_mapping: Record<string, string> = {};
    currentFormItems.forEach((item, index) => {
      const targetItem = selectedTargetFormItems[index];
      if (targetItem) {
        key_mapping[item.key_name] = targetItem.key_name;
      }
    });

    const payload = {
      lead_ids: leadIdsArray,
      campaign_id: targetCampaign.proposal_id,
      lead_form_id: String(targetForm.value),
      key_mapping,
    };

    try {
      const res = await action.moveCampaign(payload);
      if (res?.status === 200 || res?.status === true) {
        showSnackBar('Leads moved successfully', 'success');
        onClose();
      } else {
        showSnackBar('Failed to move leads', 'error');
      }
    } catch (err) {
      showSnackBar('Something went wrong', 'error');
    }
  };

  return (
    <ModalForms
      show={open}
      handleClose={onClose}
      modalHeader="Move Leads"
      size="md"
      modalBody={
        <Box>
          <Grid container spacing={3}>
            {/* CURRENT CAMPAIGN */}
            <Grid item xs={12} md={6}>
              <Typography mb={1}>Current Campaign</Typography>
              <CampaignAssignedDropdown
                value={currentCampaign}
                onChange={() => { }}
                organisationId={organisationId}
                disabled
              />

              <Box mt={2}>
                <SearchableDropdown
                  label="Current Form"
                  options={currentFormsOptions}
                  value={currentForm}
                  onChange={() => { }}
                  disabled
                />
              </Box>

              <Box mt={2}>
                {currentFormItems.map((item) => (
                  <Box key={item.key_name} mt={2}>
                    <SearchableDropdown
                      label={item.alias_name}
                      options={currentFormItems}
                      value={item}
                      onChange={() => { }}
                      disabled
                    />
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* TARGET CAMPAIGN */}
            <Grid item xs={12} md={6}>
              <Typography mt={3} mb={1}>
                Move To Campaign
              </Typography>
              <CampaignAssignedDropdown
                value={targetCampaign}
                onChange={handleTargetCampaignChange}
                organisationId={organisationId}
              />

              <Box mt={2}>
                <SearchableDropdown
                  label="Move To Form"
                  options={targetForms}
                  value={targetForm}
                  onChange={handleTargetFormChange}
                  disabled={!targetCampaign || loadingForms}
                />
              </Box>

              <Box mt={2}>
                {currentFormItems.map((item, index) => (
                  <Box key={item.key_name} mt={2}>
                    <SearchableDropdown
                      label={`Move ${item.alias_name}`}
                      options={targetFormItems}
                      value={selectedTargetFormItems[index] || null}
                      onChange={(val) => {
                        const updated = [...selectedTargetFormItems];
                        updated[index] = val;
                        setSelectedTargetFormItems(updated);
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      }
      modalFooter={
        <Box display="flex" justifyContent="flex-end" gap={1}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleMove}
          // disabled={!targetCampaign || !targetForm || isSameCampaign}
          >
            Move
          </Button>
        </Box>
      }
    />
  );
};

export default MoveToModal;
